/**
 * I-GTCC-COCKPIT-SERIES1 slots — the two Executive Summary ChartPanels
 * ("Latenz p95 24 h", "Telemetrie-Eingang Positionen/min 24 h") and the
 * "Alert Severity 7 Tage" donut. Pure SVG, no charting dependency, mirrors
 * the reference's inline `<svg class="spark">`/donut markup 1:1.
 */
import { useLocale } from "@genius-tracker/i18n/react";
import type { ReactNode } from "react";

import type {
  GtccAlertSeverityEnvelopeDto,
  GtccLatencySeriesDto,
  GtccSeriesPointDto,
  GtccTelemetryAnnotationDto,
} from "../../../exec-types.js";

/**
 * A malformed (but 200 OK) response can send `null`, an object, or omit a
 * field entirely where the DTO type promises an array. That is the source's
 * error, not a reason to crash: the empty list is what "nicht messbar" looks
 * like for a chart panel (Owner P9/P10, see `DplanePanels.tsx`).
 */
function asList<T>(value: ReadonlyArray<T> | null | undefined): ReadonlyArray<T> {
  return Array.isArray(value) ? value : [];
}

function normalizePoints(
  points: ReadonlyArray<GtccSeriesPointDto> | null | undefined,
  width: number,
  height: number,
): string {
  const list = asList(points);
  if (list.length === 0) return "";
  const values = list.map((p) => p.v);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const step = width / Math.max(list.length - 1, 1);
  return list
    .map(
      (p, i) => `${(i * step).toFixed(1)},${(height - ((p.v - min) / range) * height).toFixed(1)}`,
    )
    .join(" ");
}

const LATENCY_COLORS: Record<GtccLatencySeriesDto["key"], string> = {
  api: "var(--gt-sys-accent, var(--gt-sys-info))",
  db: "var(--gt-sys-info)",
};

const legendStyle = {
  display: "flex",
  gap: 12,
  flexWrap: "wrap" as const,
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-text-muted)",
  marginBottom: 6,
};

const legendSwatchStyle = {
  display: "inline-block",
  width: 9,
  height: 3,
  borderRadius: 2,
  marginRight: 5,
  verticalAlign: 2,
};

export function LatencyChartPanel(props: {
  series: ReadonlyArray<GtccLatencySeriesDto>;
  legendLabels: Record<GtccLatencySeriesDto["key"], string>;
  testId: string;
  ariaLabel: string;
}): ReactNode {
  const width = 300;
  const height = 60;
  return (
    <div data-testid={props.testId}>
      <div className="legend" style={legendStyle}>
        {props.series.map((s, index) => (
          <span key={`${s.key}-${index}`}>
            <b
              aria-hidden="true"
              style={{ ...legendSwatchStyle, background: LATENCY_COLORS[s.key] }}
            />
            {props.legendLabels[s.key]}
          </span>
        ))}
      </div>
      <svg
        width="100%"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={props.ariaLabel}
      >
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="var(--gt-sys-border)"
          strokeWidth={1}
        />
        {props.series.map((s, index) => {
          const points = asList(s.points).filter(
            (p) => typeof p.v === "number" && !Number.isNaN(p.v),
          );
          const coords = normalizePoints(points, width, height);
          const last = points.at(-1);
          const lastY =
            last === undefined
              ? undefined
              : height -
                ((last.v - Math.min(...points.map((p) => p.v))) /
                  (Math.max(...points.map((p) => p.v)) - Math.min(...points.map((p) => p.v)) ||
                    1)) *
                  height;
          return (
            <g key={`${s.key}-${index}`}>
              <polyline
                points={coords}
                fill="none"
                stroke={LATENCY_COLORS[s.key]}
                strokeWidth={2}
              />
              {s.key === "api" && lastY !== undefined ? (
                <circle cx={width} cy={lastY} r={3} fill={LATENCY_COLORS.api} />
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function TelemetryIngressChartPanel(props: {
  points: ReadonlyArray<GtccSeriesPointDto>;
  annotations: ReadonlyArray<GtccTelemetryAnnotationDto>;
  totalLabel: string;
  captionLabel: string;
  testId: string;
  ariaLabel: string;
}): ReactNode {
  const width = 300;
  const height = 70;
  const points = asList(props.points).filter((p) => typeof p.v === "number" && !Number.isNaN(p.v));
  const annotations = asList(props.annotations);
  const coords = normalizePoints(points, width, height);
  const values = points.map((p) => p.v);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const dipIndex = points.reduce((worst, p, i) => (p.v < points[worst]!.v ? i : worst), 0);
  const dipX = points.length > 1 ? (dipIndex * width) / (points.length - 1) : width / 2;
  const dipY =
    points.length === 0
      ? height / 2
      : height - ((values[dipIndex]! - min) / (max - min || 1)) * height;
  const showDip = annotations.length > 0 && points.length > 0;
  return (
    <div data-testid={props.testId}>
      <div className="legend" style={legendStyle}>
        <span>
          <b
            aria-hidden="true"
            style={{ ...legendSwatchStyle, background: "var(--gt-sys-accent, var(--gt-sys-info))" }}
          />
          {props.totalLabel}
        </span>
        {showDip ? (
          <span style={{ fontFamily: "var(--gt-sys-font-family-mono)" }}>{props.captionLabel}</span>
        ) : null}
      </div>
      <svg
        width="100%"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={props.ariaLabel}
      >
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="var(--gt-sys-border)"
          strokeWidth={1}
        />
        <polyline
          points={coords}
          fill="none"
          stroke="var(--gt-sys-accent, var(--gt-sys-info))"
          strokeWidth={2}
        />
        {showDip ? <circle cx={dipX} cy={dipY} r={3} fill="var(--gt-sys-warning)" /> : null}
      </svg>
      {annotations.map((a, i) => (
        <p
          key={i}
          style={{
            margin: 0,
            fontSize: "var(--gt-sys-font-size-xs)",
            color: "var(--gt-sys-warning)",
            fontFamily: "var(--gt-sys-font-family-mono)",
          }}
        >
          {a.from}–{a.to} {a.label}
        </p>
      ))}
    </div>
  );
}

const SEVERITY_COLORS: Record<"critical" | "high" | "medium" | "low", string> = {
  critical: "var(--gt-sys-danger)",
  high: "var(--gt-sys-warning)",
  medium: "var(--gt-sys-info)",
  low: "var(--gt-sys-accent, var(--gt-sys-success))",
};

export function AlertSeverityDonut(props: {
  data: GtccAlertSeverityEnvelopeDto;
  legendLabels: Record<"critical" | "high" | "medium" | "low", string>;
  testId: string;
}): ReactNode {
  const { t } = useLocale();
  const { bySeverity = { critical: 0, high: 0, medium: 0, low: 0 }, total = 0 } = props.data ?? {};
  const order: ReadonlyArray<"critical" | "high" | "medium" | "low"> = [
    "critical",
    "high",
    "medium",
    "low",
  ];
  const circumference = 2 * Math.PI * 15.9;
  let offset = 0;
  const segments = order.map((key) => {
    const value = bySeverity[key];
    const pct = total > 0 ? (value / total) * 100 : 0;
    const dash = (pct / 100) * circumference;
    const seg = {
      key,
      value,
      pct,
      dashArray: `${dash.toFixed(1)} ${(circumference - dash).toFixed(1)}`,
      dashOffset: -offset,
    };
    offset += dash;
    return seg;
  });

  return (
    <div data-testid={props.testId} style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <svg width={92} height={92} viewBox="0 0 42 42" role="img" aria-label={String(total)}>
        <circle
          cx={21}
          cy={21}
          r={15.9}
          fill="none"
          stroke="var(--gt-sys-bg-subtle)"
          strokeWidth={6}
        />
        {segments.map((s) =>
          s.value > 0 ? (
            <circle
              key={s.key}
              cx={21}
              cy={21}
              r={15.9}
              fill="none"
              stroke={SEVERITY_COLORS[s.key]}
              strokeWidth={6}
              strokeDasharray={s.dashArray}
              strokeDashoffset={s.dashOffset}
            />
          ) : null,
        )}
        <text x={21} y={20} textAnchor="middle" fontSize={9} fontWeight={700}>
          {total}
        </text>
      </svg>
      <dl style={{ margin: 0 }}>
        {segments.map((s) => (
          <div
            key={s.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: "var(--gt-sys-font-size-xs)",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: SEVERITY_COLORS[s.key],
                display: "inline-block",
              }}
            />
            <span>
              {props.legendLabels[s.key]} {s.value} ({Math.round(s.pct)} %)
            </span>
          </div>
        ))}
        <p
          style={{
            margin: 0,
            color: "var(--gt-sys-text-muted)",
            fontFamily: "var(--gt-sys-font-family-mono)",
          }}
        >
          {t("admin.gtcc.cockpit.exec.severity_ack_resolved", {
            acknowledged: props.data?.acknowledged ?? 0,
            resolved: props.data?.resolved ?? 0,
          })}
        </p>
      </dl>
    </div>
  );
}
